<script setup lang="ts">
import { useEventListener } from '@vueuse/core';
import { useChatPageTracking } from '~/composables/useChatAssistant';
import { useChatPanelStore } from '~/store/chatPanel';
import { useSnackbarStore } from '~/store/snackbar';
import { isChatShortcut } from '~/utils/chat-panel';
import { usePageHeading } from '~/libs/admin-web-common/composables/usePageHeading';

const snackbarStore = useSnackbarStore();

// The chat panel lives in the layout, so it stays open and keeps its conversation across pages.
const chatVisible = useChatAssistantVisible();
// A setup binding named `chatPanel` would shadow the ChatPanel component in the template.
const chatPanelStore = useChatPanelStore();
const route = useRoute();
const { heading } = usePageHeading();
useChatPageTracking(() => ({ path: route.path, title: heading.value?.title ?? '' }));

// The router resets the scroll position of the window only. The page scrolls inside VMain, thus a
// new page starts at the top here.
const main = useTemplateRef<{ $el: HTMLElement }>('main');
watch(
  () => route.path,
  () => main.value?.$el.querySelector('.v-main__scroller')?.scrollTo({ top: 0 }),
);

useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (!chatVisible.value || !isChatShortcut(event)) return;
  event.preventDefault();
  chatPanelStore.toggle();
});
</script>

<template>
  <VApp theme="light">
    <!-- App bar + sidebar are layout items outside VMain so they register
         with Vuetify's layout system: header spans full width at top,
         sidebar sits below header on the left. -->
    <admin-header />

    <chat-panel v-if="chatVisible" />
    <page-guide-spotlight v-if="chatVisible" />

    <!-- The page scrolls inside VMain, not in the window. The scrollbar then sits at the right
         edge of the page, left of the docked chat panel. -->
    <VMain
      ref="main"
      scrollable
      class="bg-app"
    >
      <v-snackbar
        v-model="snackbarStore.active"
        :color="snackbarStore.color"
        location="top center"
        @on-vnode-unmounted="snackbarStore.active = false"
      >
        {{ snackbarStore.content }}
      </v-snackbar>

      <div class="layout-content">
        <slot />
      </div>
    </VMain>
  </VApp>
</template>

<style lang="scss" scoped>
.bg-app {
  // Warm DS canvas behind cards — the 1px card hairline is enough separation here.
  background-color: var(--color-canvas) !important;
  // VMain is a flex item (flex-grow: 1) but display: block by default.
  // Making it flex column lets layout-content grow to fill the full viewport height.
  display: flex !important;
  flex-direction: column;
}

// VMain sets the layout variables to 0 in its scroller. FormActionGroup is fixed to the page area
// and reads them, thus the scroller takes the values of VMain again.
.bg-app :deep(.v-main__scroller) {
  --v-layout-left: inherit;
  --v-layout-right: inherit;
  --v-layout-top: inherit;
  --v-layout-bottom: inherit;
  // The page fills the scroller height, so a short form still has its action bar at the bottom.
  display: flex;
  flex-direction: column;
}

.layout-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}
</style>

<style>
/* Vuetify gives the window a scrollbar at all times. The page scrolls inside VMain in this
   layout, thus that window scrollbar shows only an empty track at the right edge of the chat. */
html:has(.v-main--scrollable) {
  overflow-y: hidden;
}
</style>
